import { auth, storage } from "config/firebase.config";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
  UploadResult,
} from "firebase/storage";

export interface UploadOptions {
  folder?: string;
  fileName?: string;
  generateUniqueFileName?: boolean;
}

export class FirebaseStorageService {
  /**
   * Upload a file to Firebase Storage
   */
  static async uploadFile(
    file: File,
    options: UploadOptions = {},
  ): Promise<string> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error("User must be authenticated to upload files");
      }

      const {
        folder = "profile-images",
        fileName = file.name,
        generateUniqueFileName = true,
      } = options;

      // Generate a unique file name if requested
      const finalFileName = generateUniqueFileName
        ? this.generateUniqueFileName(fileName)
        : fileName;

      // Create the storage reference
      const storagePath = `${folder}/${user.uid}/${finalFileName}`;
      const storageRef = ref(storage, storagePath);

      // Upload the file
      const uploadResult: UploadResult = await uploadBytes(storageRef, file);

      // Get the download URL
      const downloadURL = await getDownloadURL(uploadResult.ref);

      console.log("File uploaded successfully:", {
        path: storagePath,
        downloadURL,
        size: file.size,
        type: file.type,
      });

      return downloadURL;
    } catch (error) {
      console.error("Error uploading file:", error);
      throw new Error("Failed to upload file to storage");
    }
  }

  /**
   * Upload a profile image with specific optimization
   */
  static async uploadProfileImage(file: File): Promise<string> {
    try {
      // Validate file type
      if (!this.isValidImageFile(file)) {
        throw new Error(
          "Invalid file type. Please upload JPG, PNG, GIF, or WebP images only.",
        );
      }

      // Validate file size (5MB limit)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        throw new Error("File size too large. Maximum file size is 5MB.");
      }

      return await this.uploadFile(file, {
        folder: "admin-profile-images",
        fileName: `profile-${Date.now()}.${this.getFileExtension(file.name)}`,
        generateUniqueFileName: false,
      });
    } catch (error) {
      console.error("Error uploading profile image:", error);
      throw error;
    }
  }

  /**
   * Delete a file from Firebase Storage
   */
  static async deleteFile(fileUrl: string): Promise<void> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error("User must be authenticated to delete files");
      }

      // Extract the storage path from the URL
      const storagePath = this.extractStoragePathFromUrl(fileUrl);
      if (!storagePath) {
        throw new Error("Invalid file URL");
      }

      const storageRef = ref(storage, storagePath);
      await deleteObject(storageRef);

      console.log("File deleted successfully:", storagePath);
    } catch (error) {
      console.error("Error deleting file:", error);
      throw new Error("Failed to delete file from storage");
    }
  }

  /**
   * Delete old profile image when uploading a new one
   */
  static async replaceProfileImage(
    newFile: File,
    oldImageUrl?: string,
  ): Promise<string> {
    // Upload the new image first
    const newImageUrl = await this.uploadProfileImage(newFile);

    // Delete the old image if it exists
    if (oldImageUrl) {
      await this.deleteFile(oldImageUrl);
    }

    return newImageUrl;
  }

  /**
   * Generate a unique file name with timestamp and random string
   */
  private static generateUniqueFileName(originalName: string): string {
    const timestamp = Date.now();
    const randomString = Math.random().toString(36).substring(2, 8);
    const extension = this.getFileExtension(originalName);
    return `${timestamp}_${randomString}.${extension}`;
  }

  /**
   * Get file extension from file name
   */
  private static getFileExtension(fileName: string): string {
    return fileName.split(".").pop() || "jpg";
  }

  /**
   * Extract storage path from Firebase Storage URL
   */
  private static extractStoragePathFromUrl(url: string): string | null {
    try {
      // Firebase Storage URLs have this pattern:
      // https://firebasestorage.googleapis.com/v0/b/{bucket}/o/{path}?alt=media&token={token}
      const urlObj = new URL(url);
      const pathParam =
        urlObj.searchParams.get("o") || urlObj.pathname.split("/o/")[1];

      if (pathParam) {
        return decodeURIComponent(pathParam.split("?")[0]);
      }

      return null;
    } catch {
      return null;
    }
  }

  /**
   * Validate if file is a valid image type
   */
  private static isValidImageFile(file: File): boolean {
    const validTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp",
    ];
    return validTypes.includes(file.type);
  }

  /**
   * Batch upload multiple files
   */
  static async uploadMultipleFiles(
    files: File[],
    options: UploadOptions = {},
  ): Promise<string[]> {
    const uploadPromises = files.map((file) => this.uploadFile(file, options));

    try {
      const urls = await Promise.all(uploadPromises);
      return urls;
    } catch {
      throw new Error("One or more files failed to upload");
    }
  }
}
